<template>
  <div class="cat-stepper">
    <div class="stepper-nav">
      <div class="side-content">
        <div id="sticky-wrapper" class="sticky-wrapper">
          <div :id="id + '_sticky'" class="sticky">
            <div
              v-if="showProgress && !suppressSteps"
              style="margin-bottom: 0.2em"
              data-label-type="progress-indicator-title"
              role="heading"
              aria-level="4"
              class="h4 tab-focus"
            >
              {{ progressIndicatorTitle }}
            </div>

            <div v-if="showProgress && !suppressSteps" class="progress">
              <div class="progress-bar">
                <div
                  class="inner-bar"
                  :style="
                    'width: calc(' +
                    (numCompleted + 1) +
                    ' * 100% /' +
                    items.length +
                    ');'
                  "
                ></div>
              </div>
            </div>
            <slot name="fees"></slot>
            <ul
              v-if="!suppressSteps"
              :class="wizardCss"
              class="cat-stepper-link-list"
              :role="!restrictMovement ? 'tablist' : undefined"
              :aria-label="resolveTxt('skin.stepper.steps', 'Steps')"
            >
              <li
                v-for="(item, idx) in items"
                :key="idx"
                :class="detailedItems[idx].style"
                :role="!restrictMovement ? 'presentation' : undefined"
              >
                <a
                  v-if="!restrictMovement || item.step <= maxStep"
                  :id="id + '_tab_' + item.step"
                  :role="!restrictMovement ? 'tab' : undefined"
                  :aria-selected="
                    !restrictMovement
                      ? item.step === sel
                        ? 'true'
                        : 'false'
                      : undefined
                  "
                  :aria-controls="
                    !restrictMovement ? id + '_tab_panel' : undefined
                  "
                  :aria-current="
                    restrictMovement && item.step === sel ? 'step' : undefined
                  "
                  data-label-type="step-title"
                  :data-label="item.title"
                  :tabindex="
                    !restrictMovement ? (item.step === sel ? '0' : '-1') : '0'
                  "
                  class="cat-stepper-link tab-focus"
                  @click="_onClick($event, item.step)"
                  @keyup.enter="_onClick($event, item.step)"
                  @keydown.space.prevent="_onClick($event, item.step)"
                  @keydown.left.prevent="
                    !restrictMovement
                      ? previousStep($event, item.step)
                      : undefined
                  "
                  @keydown.right.prevent="
                    !restrictMovement ? nextStep($event, item.step) : undefined
                  "
                  @keydown.up.prevent="
                    !restrictMovement
                      ? previousStep($event, item.step)
                      : undefined
                  "
                  @keydown.down.prevent="
                    !restrictMovement ? nextStep($event, item.step) : undefined
                  "
                  @keydown.tab="
                    !restrictMovement
                      ? focusTabPanel($event, $el, $refs.stepperContent)
                      : undefined
                  "
                >
                  <cat-icon
                    v-if="detailedItems[idx].icon"
                    :class="detailedItems[idx].style"
                    :decorative="false"
                    :icon="detailedItems[idx].icon"
                    :iconLabel="detailedItems[idx].statusCaption"
                  >
                  </cat-icon>
                  <span class="step-number-wrapper mrs"
                    ><span class="visually-hidden">{{
                      stepCaption(item.step)
                    }}</span>
                    <span aria-hidden="true" class="step-number"
                      >{{ item.step }}.</span
                    ></span
                  >
                  <span
                    class="step-name"
                    :class="item.step === sel ? 'active' : null"
                    >{{ item.title }}</span
                  >
                </a>
                <div
                  v-else
                  :id="id + '_tab_' + item.step"
                  tabindex="0"
                  class="cat-stepper-link tab-focus"
                  :aria-current="item.step === sel ? 'step' : undefined"
                  :aria-disabled="true"
                  data-label-type="step-title"
                  :data-label="item.title"
                >
                  <span class="step-number-wrapper mrs"
                    ><span class="visually-hidden">{{
                      stepCaption(item.step)
                    }}</span
                    ><span aria-hidden="true" class="step-number"
                      >{{ item.step }}.</span
                    ></span
                  >
                  <span class="step-name disabled">{{ item.title }}</span>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <div
      ref="stepperContent"
      :id="!restrictMovement ? id + '_tab_panel' : undefined"
      class="stepper-content"
      :role="!restrictMovement ? 'tabpanel' : undefined"
      :aria-labelledby="!restrictMovement ? id + '_tab_' + sel : undefined"
      :tabindex="!restrictMovement && !panelHasFocusable ? '0' : undefined"
    >
      <div class="main-content" :class="tabStepper ? 'mtn' : null">
        <p :id="`cat-stepper-selected-title-${id}`" class="screen-reader-text">
          {{ selectedTitle }}
        </p>
        <slot></slot>
        <slot name="buttonpad"></slot>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import { defaultResolveTxt } from "@/components/field-element";
import jQuery from "jquery";
import CatIcon from "@/components/utilities/catIcon.vue";
import TabsMixin from "../utilities/tabsMixin.js";

export default defineComponent({
  setup() {
    // Inject the global value
    const resolveTxt = inject("resolveTxt", defaultResolveTxt);
    const displayParams = inject("displayParams", false);
    const addContentClass = inject("addContentClass", null);

    return {
      resolveTxt,
      displayParams,
      addContentClass
    };
  },
  name: "CatStepper",
  components: { CatIcon },
  mixins: [TabsMixin],
  props: {
    id: {
      type: [String, Number]
    },
    items: {
      type: [Array]
    },
    selected: [Number],
    restrictMovement: {
      type: Boolean,
      default: true
    },
    stepAlertDisplayLevel: {
      type: [String, Number],
      default: 1,
      description:
        "This will indicate the types of alert icon available to show up in the stepper links. Depending on the status of the form section, different icon will show."
    },
    showProgress: {
      type: Boolean,
      default: true
    },
    tabStepper: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      sel: this.selected ? this.selected : 1,
      maxStep: this.maxSteps(),
      responsiveShow: true,
      panelHasFocusable: true
    };
  },
  watch: {
    selected: function (newsel, oldsel) {
      this.sel = newsel;
      this.maxStep = this.maxSteps();

      // We re moving to different tab, focus to the heading so screen reader will be notified of the content change
      this.$nextTick(function () {
        this.focusHeading();

        // Update the document title with the current step
        this.updateDocumentTitle();

        this.navigateStep();
        this._checkPanelFocusable();
      });
    }
  },

  created: function () {
    this._default = this.selected ? this.selected : this.items[0].step;
  },

  mounted: function () {
    if (this.addContentClass) {
      this.addContentClass("has-side-content");
    }

    // Set the focus on the heading, this is required if coming in from before you begin page
    this.$nextTick(function () {
      this.focusHeading(true);
      this.updateDocumentTitle();
      this._checkPanelFocusable();
    });
  },

  updated: function () {
    this._checkPanelFocusable();
  },

  methods: {
    _checkPanelFocusable: function () {
      if (this.restrictMovement || !this.$refs.stepperContent) return;
      this.panelHasFocusable =
        window.jQuery(":tabbable", this.$refs.stepperContent).length > 0;
    },
    _onClick: function (event, step) {
      // console.log("Clicked on stepper", event, step);
      this.$emit("ui-click", {
        event: event,
        value: step,
        id: this.id ? this.id : null
      });
    },
    /**
     * @param event
     * @param step one-based index
     */
    nextStep: function (event, step) {
      const index = step === this.items.length ? 1 : step + 1;
      this.focusStep(index - 1);
    },
    /**
     * @param event
     * @param step one-based index
     */
    previousStep: function (event, step) {
      const index = step === 1 ? this.items.length : step - 1;
      this.focusStep(index - 1);
    },
    /**
     * @param index zero-based index
     */
    focusStep: function (index) {
      jQuery(".cat-stepper-link", this.$el).eq(index).trigger("focus");
    },
    maxSteps: function () {
      let maxVisited = 0;
      this.items.forEach((element) => {
        if (element.visited) {
          maxVisited = parseInt(element.step);
        }
      });
      if (this.selected < maxVisited) {
        return maxVisited;
      }
      return this.selected;
    },
    getColor: function (item) {
      return item.complete ? "primary" : "transparent";
    },
    _getRules: function (item) {
      return [() => !item.errors.length > 0];
    },
    save: function () {
      Catalyst.save(this.id);
    },
    navigateStep: function () {
      // Focus on the stepper content
      const content = this.$refs.stepperContent;
      const viewTop = window.scrollY || document.documentElement.scrollTop;
      const headerHeight = document.querySelector("header")
        ? document.querySelector("header").offsetHeight
        : 0;
      const neededOffset = content.offsetTop - headerHeight;

      // Scroll and focus disabled in UI/Hercules Test
      if (!window.isTestMode) {
        if (neededOffset < viewTop && !window.isTestMode) {
          jQuery("html, body").animate({ scrollTop: neededOffset }, 200);
        }

        this.focusHeading(false);
      }
    },

    focusHeading(isMounted) {
      // Focus on the first section title inside the stepper content slot
      // and focus to role=heading if no section-title found

      const sectionTitle = jQuery(
        "[data-label-type=section-title]",
        this.$refs.stepperContent
      );
      const heading = jQuery("[role=heading]", this.$refs.stepperContent);

      if (sectionTitle.length > 0) {
        const firstTitle = sectionTitle.first();
        firstTitle.attr(
          "aria-labelledby",
          `cat-stepper-selected-title-${this.id}`
        );

        // Focus only if the component is mounted
        if (!isMounted) {
          firstTitle.focus();
        }
      } else if (heading.length > 0 && !isMounted) {
        // Only focus on the heading if not mounted
        heading.first().focus();
      }
    },
    /**
     * Returns the additional details on the item
     * - icon
     * - statusCaption
     * - style
     */
    itemDetails: function (item) {
      var detail = {};
      if (item.step === this.sel) {
        // See if this step is the currently selected
        detail = {
          statusCaption: this.resolveTxt(
            "skin.stepper.step.current",
            "Current"
          ),
          icon: "angle-right",
          style: "active"
        };
      } else if (item.editable && item.invalid) {
        detail = {
          statusCaption: this.resolveTxt(
            "skin.stepper.step.incomplete",
            "Incomplete"
          ),
          icon: "attention",
          style: "incomplete"
        };
      } else if (
        item.editable &&
        item.warning &&
        this.stepAlertDisplayLevel >= 2
      ) {
        detail = {
          statusCaption: this.resolveTxt(
            "skin.stepper.step.warning",
            "Warning"
          ),
          icon: "attention-circled",
          style: "warning"
        };
      } else if (
        item.editable &&
        item.infomessage &&
        this.stepAlertDisplayLevel >= 3
      ) {
        detail = {
          statusCaption: this.resolveTxt(
            "skin.stepper.step.info",
            "Has information"
          ),
          icon: "info-circled",
          style: "infomessage"
        };
      } else if (item.debugmessage && this.stepAlertDisplayLevel >= 4) {
        detail = {
          statusCaption: this.resolveTxt(
            "skin.stepper.step.debug",
            "Has debug information"
          ),
          icon: "edit",
          style: "debugmessage"
        };
      } else if (item.complete) {
        detail = {
          statusCaption: this.resolveTxt(
            "skin.stepper.step.complete",
            "Completed"
          ),
          icon: "ok-circled",
          style: "complete"
        };
      }
      return detail;
    },
    /**
     * @param index one-based index
     * @returns {string}
     */
    stepCaption: function (index) {
      return this.resolveTxt("skin.stepper.step", "Step") + " " + index + " ";
    },
    /**
     * Updates the document title to include the current step
     */
    updateDocumentTitle: function () {
      if (!this.items || !this.items.length) {
        return;
      }

      const currentTitle = document.title;
      const stepText =
        this.resolveTxt("skin.default.step", "Step") +
        " " +
        this.sel +
        " " +
        this.resolveTxt("skin.default.of", "of") +
        " " +
        this.items.length;

      // Check if title already contains a step indicator (after pipe)
      if (
        currentTitle.includes(
          " | " + this.resolveTxt("skin.default.step", "Step")
        )
      ) {
        // Replace the existing step indicator
        document.title = currentTitle.replace(
          new RegExp(
            ` \\| ${this.resolveTxt("skin.default.step", "Step")} .* ${this.resolveTxt("skin.default.of", "of")} .*$`
          ),
          " | " + stepText
        );
      } else {
        // Add the step indicator
        document.title = currentTitle + " | " + stepText;
      }
    }
  },
  computed: {
    numCompleted: function () {
      return this.items.reduce(
        (acc, cur, idx) => acc + (cur.complete ? 1 : 0),
        0
      );
    },
    suppressSteps: function () {
      return this.items.length == 1;
    },
    wizardCss: function () {
      return {
        wizard: true,
        visible: this.responsiveShow,
        mtt: this.responsiveShow
      };
    },
    /**
     * The items prop containing additional details ie. styling, icon, accessible names
     * @returns {unknown[]}
     */
    detailedItems: function () {
      var t = this;
      return this.items.map(function (item) {
        var detail = t.itemDetails(item);
        return {
          step: item.step,
          icon: detail.icon,
          statusCaption: detail.statusCaption,
          style: detail.style
        };
      });
    },
    progressIndicatorTitle: function () {
      return `${this.resolveTxt("skin.default.step", "Step")} ${this.selected} ${this.resolveTxt("skin.default.of", "of")} ${this.items.length}`;
    },
    selectedTitle: function () {
      const currentArray = this.selected ? this.selected - 1 : 0;
      if (currentArray < 0) {
        return "";
      }
      return `${this.progressIndicatorTitle} ${this.items[currentArray].title}`;
    }
  }
});
</script>
